
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, effect } from '@angular/core';
import { CommonModule, DatePipe, JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuditService, AuditLog, AuditFilter } from '../../services/audit.service';
import { DataService } from '../../services/data.service';
import { AppUser } from '../../models/index';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, JsonPipe, TranslatePipe],
  templateUrl: './audit-logs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogsComponent implements OnInit {
  private auditService = inject(AuditService);
  private dataService = inject(DataService);

  // Data
  logs = signal<AuditLog[]>([]);
  users = signal<AppUser[]>([]);
  totalCount = signal(0);
  
  // State
  isLoading = signal(false);
  expandedLogId = signal<string | null>(null);

  // Filter State
  filter = signal<AuditFilter>({
      actionType: 'ALL',
      userId: 'all',
      searchTerm: '',
      page: 1,
      pageSize: 20,
      startDate: undefined,
      endDate: undefined
  });
  
  // Date Presets
  datePreset = signal<'today' | '7d' | '30d'>('today');

  constructor() {
      // Auto-reload when filter changes (debounced slightly in real apps, here simple effect)
      effect(() => {
          this.loadLogs();
      }, { allowSignalWrites: true });
  }

  ngOnInit() {
      this.loadUsers();
      this.setDatePreset('today'); // Set default date
  }

  async loadUsers() {
      try {
          this.users.set(await this.dataService.getUsers());
      } catch (e) {
          console.error('Failed to load users for filter', e);
      }
  }

  setDatePreset(preset: 'today' | '7d' | '30d') {
      this.datePreset.set(preset);
      const end = new Date();
      const start = new Date();
      
      if (preset === 'today') start.setHours(0,0,0,0);
      if (preset === '7d') start.setDate(end.getDate() - 7);
      if (preset === '30d') start.setDate(end.getDate() - 30);

      this.filter.update(f => ({ ...f, startDate: start.toISOString(), endDate: end.toISOString(), page: 1 }));
  }

  async loadLogs() {
      this.isLoading.set(true);
      try {
          const result = await this.auditService.getAuditLogs(this.filter());
          this.logs.set(result.logs || []);
          this.totalCount.set(result.total_count || 0);
      } catch (e) {
          console.error("Error loading logs", e);
      } finally {
          this.isLoading.set(false);
      }
  }

  // --- Filter Helper Methods (Fix for Template Parser Errors) ---
  
  updateUserFilter(userId: string) {
      this.filter.update(f => ({ ...f, userId, page: 1 }));
  }

  updateActionTypeFilter(actionType: string) {
      this.filter.update(f => ({ ...f, actionType, page: 1 }));
  }

  updateSearchTerm(searchTerm: string) {
      this.filter.update(f => ({ ...f, searchTerm, page: 1 }));
  }

  nextPage() {
      if (this.filter().page * this.filter().pageSize < this.totalCount()) {
          this.filter.update(f => ({ ...f, page: f.page + 1 }));
      }
  }

  prevPage() {
      if (this.filter().page > 1) {
          this.filter.update(f => ({ ...f, page: f.page - 1 }));
      }
  }

  toggleMetadata(id: string) {
      this.expandedLogId.update(current => current === id ? null : id);
  }

  exportCsv() {
      const logs = this.logs();
      if (!logs.length) return;
      
      const header = ['Date', 'User', 'Role', 'Action', 'Description', 'Metadata'];
      const csv = [
          header.join(','),
          ...logs.map(l => [
              `"${new Date(l.created_at).toLocaleString()}"`,
              `"${l.user_name}"`,
              `"${l.user_role}"`,
              `"${l.action_type}"`,
              `"${l.description.replace(/"/g, '""')}"`,
              `"${JSON.stringify(l.metadata).replace(/"/g, '""')}"`
          ].join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString()}.csv`;
      a.click();
  }

  // Visual Helpers
  getActionColor(type: string): string {
      if (type.includes('VIEW')) return 'bg-blue-100 text-blue-700 border-blue-200';
      if (type.includes('CREATE') || type.includes('ADD')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      if (type.includes('UPDATE') || type.includes('EDIT')) return 'bg-orange-100 text-orange-700 border-orange-200';
      if (type.includes('DELETE') || type.includes('REMOVE')) return 'bg-red-100 text-red-700 border-red-200';
      if (type.includes('LOGIN')) return 'bg-purple-100 text-purple-700 border-purple-200';
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}
